// src/shared/api/executive-monitoring.api.ts
import axios from 'axios'
import type { AxiosResponse } from 'axios'

export interface SimplifiedSiteStatus {
  id: string
  name: string
  status: 'working' | 'problem' | 'not_working'
  problemSince?: string
  lastCheck: string
}

export interface ExecutiveMonitoringResponse {
  success: boolean
  data: SimplifiedSiteStatus[]
  timestamp: string
}

interface ApiResponse<T = unknown> {
  success: boolean
  message?: string
  data: T
  timestamp: string
}

interface NestedDataStructure {
  data: SimplifiedSiteStatus[]
  timestamp: string
}

// Определяем тип для API response с возможными структурами
type ApiMonitoringResponse = 
  | ApiResponse<SimplifiedSiteStatus[]> // Вариант 1: data = SimplifiedSiteStatus[]
  | ApiResponse<NestedDataStructure>    // Вариант 2: data = { data: [...], timestamp }
  | ApiResponse<unknown>                // Вариант 3: unknown структура

export class ExecutiveMonitoringApi {
  private api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/api/executive',
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    validateStatus: (status) => status >= 200 && status < 300
  })

  /**
   * Извлекает массив сайтов из API response с безопасной типизацией
   */
  private extractSitesData(apiData: ApiMonitoringResponse): SimplifiedSiteStatus[] {
    try {
      console.log('Extracting sites data from:', apiData)
      
      const data = apiData.data
      
      // Проверяем все возможные структуры
      
      // Вариант 1: data - это уже массив сайтов
      if (Array.isArray(data)) {
        console.log('Extracted direct array:', { count: data.length })
        return data
      }
      
      // Вариант 2: data - объект с полем data (вложенная структура)
      if (
        data && 
        typeof data === 'object' && 
        'data' in data && 
        Array.isArray((data as Record<string, unknown>).data)
      ) {
        const sites = (data as Record<string, unknown>).data as SimplifiedSiteStatus[]
        console.log('Extracted nested data.data:', { count: sites.length })
        return sites
      }
      
      // Вариант 3: data - объект, но без ожидаемой структуры
      if (data && typeof data === 'object') {
        console.warn('Unexpected API response structure:', data)
      }
      
      console.warn('No valid sites data found in response')
      return []
      
    } catch (error) {
      console.error('Failed to extract sites data:', error)
      return []
    }
  }

  /**
   * Извлекает timestamp из API response
   */
  private extractTimestamp(apiData: ApiMonitoringResponse): string {
    try {
      const data = apiData.data
      
      // Если data - вложенная структура, берем timestamp оттуда
      if (
        data && 
        typeof data === 'object' && 
        'timestamp' in data && 
        typeof (data as Record<string, unknown>).timestamp === 'string'
      ) {
        return (data as Record<string, unknown>).timestamp as string
      }
      
      // Иначе берем верхний уровень timestamp
      return apiData.timestamp || new Date().toISOString()
      
    } catch (error) {
      console.error('Failed to extract timestamp:', error)
      return new Date().toISOString()
    }
  }

  /**
   * Получает данные мониторинга с ретраями
   */
  async getMonitoringData(): Promise<ExecutiveMonitoringResponse> {
    const MAX_RETRIES = 3
    let lastError: Error | null = null
    
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        console.debug(`Fetching monitoring data (attempt ${attempt}/${MAX_RETRIES})`)
        
        const response: AxiosResponse<ApiMonitoringResponse> = await this.api.get('/monitoring')
        
        console.log('Raw API Response:', response.data)
        
        if (!response.data.success) {
          throw new Error(`API returned error: ${response.data.message || 'Unknown error'}`)
        }
        
        const sites = this.extractSitesData(response.data)
        const timestamp = this.extractTimestamp(response.data)
        
        console.info('Monitoring data fetched successfully', {
          sitesCount: sites.length,
          timestamp,
          attempt
        })
        
        return {
          success: true,
          data: sites,
          timestamp
        }
        
      } catch (error) {
        lastError = error as Error
        
        if (attempt === MAX_RETRIES) {
          break
        }
        
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000)
        console.warn(`Retrying after error (attempt ${attempt}/${MAX_RETRIES})`, {
          error: error instanceof Error ? error.message : 'Unknown error',
          delay
        })
        
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    
    console.error('Failed to fetch monitoring data after all retries', { error: lastError })
    throw lastError || new Error('Failed to fetch monitoring data')
  }

  /**
   * Получает статус конкретного сайта
   */
  async getSiteStatus(siteId: string): Promise<SimplifiedSiteStatus | null> {
    try {
      const response: AxiosResponse<ApiResponse<unknown>> = 
        await this.api.get(`/monitoring/${siteId}`)
      
      if (!response.data.success) {
        console.warn('Failed to get site status', {
          siteId,
          message: response.data.message
        })
        return null
      }
      
      // Пытаемся извлечь данные сайта из разных структур
      const data = response.data.data
      
      if (Array.isArray(data) && data.length > 0) {
        return data[0]
      }
      
      if (data && typeof data === 'object') {
        // Пробуем найти данные в разных возможных структурах
        const possibleSiteData = 
          (data as Record<string, unknown>).data ||
          (data as Record<string, unknown>).site ||
          data
        
        if (
          possibleSiteData && 
          typeof possibleSiteData === 'object' &&
          'id' in possibleSiteData &&
          'name' in possibleSiteData
        ) {
          return possibleSiteData as SimplifiedSiteStatus
        }
      }
      
      return null
      
    } catch (error) {
      console.error('API Error getting site status', {
        siteId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      return null
    }
  }
}

// Экспортируем синглтон
export const executiveMonitoringApi = new ExecutiveMonitoringApi()